/**
 * Persistence for the post-session review — all the loads/saves the
 * SessionReview UI needs. One pass through this module keeps the tabs
 * thin and makes Brain-tool commits (Phase 3) drop-in.
 *
 * Row shapes mirror migration 00022_session_review.sql + the existing
 * ratings / sessions / session_drills / team_players tables.
 */
import { supabase } from '../supabase';
import type {
  DrillGrade,
  DrillSentiment,
  PlayerSelfFeedback,
  SessionAttendance,
  SessionReview,
} from '@4cm/shared/reviewSchema';

// ---------- helpers ----------

export interface SessionDrillRow {
  id: string;
  drill_id: string;
  sort_order: number;
  drill: { id: string; name: string } | null;
}

export interface RosterRow {
  id: string;           // team_players.id
  player_id: string;
  display_name: string;
}

// ---------- load ----------

/** Ensure a session_reviews row exists for the session and return it.
 *  First call for a session creates the draft; subsequent calls load it. */
export async function ensureReview(sessionId: string, coachId: string): Promise<SessionReview> {
  const { data: existing } = await supabase
    .from('session_reviews')
    .select('*')
    .eq('session_id', sessionId)
    .maybeSingle();
  if (existing) return existing as SessionReview;

  const { data: created, error } = await supabase
    .from('session_reviews')
    .insert({ session_id: sessionId, coach_id: coachId, status: 'draft' })
    .select('*')
    .single();
  if (error || !created) {
    throw new Error(`Failed to open review: ${error?.message ?? 'unknown'}`);
  }
  return created as SessionReview;
}

export async function loadAttendance(sessionId: string): Promise<SessionAttendance[]> {
  const { data } = await supabase
    .from('session_attendance')
    .select('*')
    .eq('session_id', sessionId);
  return (data ?? []) as SessionAttendance[];
}

export async function loadDrillGrades(sessionReviewId: string): Promise<DrillGrade[]> {
  const { data } = await supabase
    .from('drill_grades')
    .select('*')
    .eq('session_review_id', sessionReviewId);
  return (data ?? []) as DrillGrade[];
}

export async function loadSessionDrills(sessionId: string): Promise<SessionDrillRow[]> {
  const { data } = await supabase
    .from('session_drills')
    .select('id, drill_id, sort_order, drill:drills(id, name)')
    .eq('session_id', sessionId)
    .order('sort_order', { ascending: true });
  // supabase-js returns drill as array on !inner joins; normalise.
  return (data ?? []).map((r: any) => ({
    id: r.id,
    drill_id: r.drill_id,
    sort_order: r.sort_order,
    drill: Array.isArray(r.drill) ? (r.drill[0] ?? null) : r.drill,
  }));
}

export async function loadRoster(teamId: string): Promise<RosterRow[]> {
  // team_players is the roster. The users.name column holds the display
  // name (spec §02). If the join returns no user row, fall back to the
  // row id so the list still renders.
  const { data } = await supabase
    .from('team_players')
    .select('id, player_id, user:users(id, name)')
    .eq('team_id', teamId);
  return (data ?? []).map((r: any) => ({
    id: r.id,
    player_id: r.player_id,
    display_name: (Array.isArray(r.user) ? r.user[0]?.name : r.user?.name) ?? `Player ${String(r.player_id).slice(0, 4)}`,
  }));
}

export async function loadSelfFeedback(sessionId: string): Promise<PlayerSelfFeedback[]> {
  const { data } = await supabase
    .from('player_self_feedback')
    .select('*')
    .eq('session_id', sessionId);
  return (data ?? []) as PlayerSelfFeedback[];
}

// ---------- save ----------

export async function patchReview(
  reviewId: string,
  patch: Partial<Pick<SessionReview, 'notes_working' | 'notes_issues' | 'next_session_tweaks' | 'ai_narrative' | 'status' | 'completed_at'>>,
): Promise<boolean> {
  const { error } = await supabase
    .from('session_reviews')
    .update(patch)
    .eq('id', reviewId);
  return !error;
}

export async function completeReview(reviewId: string): Promise<boolean> {
  return patchReview(reviewId, { status: 'complete', completed_at: new Date().toISOString() });
}

/** Upsert attendance for one player. Uses the (session_id, player_id)
 *  unique index so repeated toggles update in place. */
export async function upsertAttendance(
  sessionId: string,
  playerId: string,
  patch: { present: boolean; reason?: string | null },
): Promise<boolean> {
  const { error } = await supabase
    .from('session_attendance')
    .upsert(
      {
        session_id: sessionId,
        player_id: playerId,
        present: patch.present,
        reason: patch.reason ?? null,
      },
      { onConflict: 'session_id,player_id' },
    );
  return !error;
}

/** Upsert a drill grade. Uses (session_review_id, session_drill_id)
 *  unique index. */
export async function upsertDrillGrade(
  reviewId: string,
  sessionDrillId: string,
  patch: { grade?: number | null; sentiment?: DrillSentiment | null; coach_notes?: string | null },
): Promise<boolean> {
  const { error } = await supabase
    .from('drill_grades')
    .upsert(
      {
        session_review_id: reviewId,
        session_drill_id: sessionDrillId,
        grade: patch.grade ?? null,
        sentiment: patch.sentiment ?? null,
        coach_notes: patch.coach_notes ?? null,
      },
      { onConflict: 'session_review_id,session_drill_id' },
    );
  return !error;
}

// ---------- player grading (writes to ratings table) ----------

export interface PlayerCornerGrade {
  ratee_id: string;      // player user id
  corner_id: string;     // corner uuid
  score: number;         // 0-100
}

/** Coach's per-corner grades for a player, for this session. Writes
 *  rating rows with context='SESSION', state='COACH_SUBMITTED' so the
 *  blind-rating state machine (§07) advances correctly. Upserts on
 *  (rater_id, ratee_id, session_id, corner_id) — but that composite
 *  isn't unique in the current schema, so we fetch + decide per row. */
export async function saveCoachPlayerGrade(args: {
  coachId: string;
  sessionId: string;
  grade: PlayerCornerGrade;
}): Promise<boolean> {
  const { coachId, sessionId, grade } = args;
  // Look up an existing rating for this coach+player+session+corner.
  const { data: existing } = await supabase
    .from('ratings')
    .select('id, state')
    .eq('rater_id', coachId)
    .eq('ratee_id', grade.ratee_id)
    .eq('ratee_type', 'PLAYER')
    .eq('context', 'SESSION')
    .eq('session_id', sessionId)
    .eq('corner_id', grade.corner_id)
    .maybeSingle();

  if (existing?.id) {
    const { error } = await supabase
      .from('ratings')
      .update({ score: grade.score, state: 'COACH_SUBMITTED' })
      .eq('id', existing.id);
    return !error;
  }

  const { error } = await supabase.from('ratings').insert({
    rater_id: coachId,
    ratee_id: grade.ratee_id,
    ratee_type: 'PLAYER',
    context: 'SESSION',
    session_id: sessionId,
    corner_id: grade.corner_id,
    score: grade.score,
    is_blind: true,
    state: 'COACH_SUBMITTED',
  });
  return !error;
}

/** Load the coach's own existing grades for this session so the tab can
 *  render with prior values filled in. */
export async function loadCoachGrades(coachId: string, sessionId: string): Promise<Array<{
  ratee_id: string;
  corner_id: string;
  score: number;
}>> {
  const { data } = await supabase
    .from('ratings')
    .select('ratee_id, corner_id, score')
    .eq('rater_id', coachId)
    .eq('ratee_type', 'PLAYER')
    .eq('context', 'SESSION')
    .eq('session_id', sessionId);
  return (data ?? []) as any;
}

// ---------- reveal (Phase 5) ----------

export interface RevealPair {
  player_id: string;
  corner_id: string;
  coach_score: number | null;
  player_score: number | null;
  /** 'REVEALED' only when both sides submitted. */
  state: string;
  delta: number | null;       // coach - player
  revealed_at: string | null;
}

/** Pair up coach + player ratings for one session so the UI can show
 *  deltas. Only rows where state='REVEALED' surface per-corner deltas;
 *  pending rows are included but with null player_score so the UI can
 *  say "waiting for player". */
export async function loadRevealPairs(
  coachId: string,
  sessionId: string,
): Promise<RevealPair[]> {
  // Fetch both sides of the pair — coach's rows and self-rated rows.
  const [coachRes, selfRes] = await Promise.all([
    supabase
      .from('ratings')
      .select('id, ratee_id, corner_id, score, state, revealed_at')
      .eq('rater_id', coachId)
      .eq('ratee_type', 'PLAYER')
      .eq('context', 'SESSION')
      .eq('session_id', sessionId),
    supabase
      .from('ratings')
      .select('id, rater_id, ratee_id, corner_id, score, state, revealed_at')
      .neq('rater_id', coachId)
      .eq('ratee_type', 'PLAYER')
      .eq('context', 'SESSION')
      .eq('session_id', sessionId),
  ]);

  // Self-ratings have rater_id = ratee_id. Supabase doesn't filter
  // column-to-column, so filter client-side.
  const selfByKey = new Map<string, any>();
  for (const r of (selfRes.data ?? []) as any[]) {
    if (r.rater_id !== r.ratee_id) continue;
    selfByKey.set(`${r.ratee_id}|${r.corner_id}`, r);
  }

  const pairs: RevealPair[] = [];
  const seenKeys = new Set<string>();
  for (const c of coachRes.data ?? []) {
    const key = `${c.ratee_id}|${c.corner_id}`;
    seenKeys.add(key);
    const self = selfByKey.get(key);
    const revealed = c.state === 'REVEALED' && self;
    pairs.push({
      player_id: c.ratee_id,
      corner_id: c.corner_id,
      coach_score: c.score,
      player_score: revealed ? self.score : null,
      state: c.state,
      delta: revealed ? (c.score - self.score) : null,
      revealed_at: c.revealed_at,
    });
  }
  // Include self-only rows (player submitted but coach hasn't).
  for (const [key, s] of selfByKey) {
    if (seenKeys.has(key)) continue;
    pairs.push({
      player_id: s.ratee_id,
      corner_id: s.corner_id,
      coach_score: null,
      player_score: s.score,
      state: s.state,
      delta: null,
      revealed_at: s.revealed_at,
    });
  }
  return pairs;
}

/** Load one player's reveal pairs for their own view. Doesn't need the
 *  coach_id filter since a player only sees their own rows. */
export async function loadPlayerRevealPairs(
  playerId: string,
  sessionId: string,
): Promise<RevealPair[]> {
  const { data } = await supabase
    .from('ratings')
    .select('rater_id, ratee_id, corner_id, score, state, revealed_at')
    .eq('ratee_id', playerId)
    .eq('ratee_type', 'PLAYER')
    .eq('context', 'SESSION')
    .eq('session_id', sessionId);

  const rows = data ?? [];
  // Group by corner: one pair per corner.
  const byCorner = new Map<string, { self?: any; coach?: any }>();
  for (const r of rows as any[]) {
    const bucket = byCorner.get(r.corner_id) ?? {};
    if (r.rater_id === r.ratee_id) bucket.self = r;
    else bucket.coach = r;
    byCorner.set(r.corner_id, bucket);
  }

  const out: RevealPair[] = [];
  for (const [cornerId, pair] of byCorner) {
    const revealed = pair.coach?.state === 'REVEALED' && pair.self;
    out.push({
      player_id: playerId,
      corner_id: cornerId,
      coach_score: revealed ? pair.coach.score : null,
      player_score: pair.self?.score ?? null,
      state: pair.coach?.state ?? pair.self?.state ?? 'PLAYER_PENDING',
      delta: revealed ? (pair.coach.score - pair.self.score) : null,
      revealed_at: pair.coach?.revealed_at ?? pair.self?.revealed_at ?? null,
    });
  }
  return out;
}
